/**
 * Sentry Error Monitoring Configuration
 * Knitido B2B Partner Portal
 * 
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = 'https://09d586412581abc671d4d1cc882e0377@o4510668103745536.ingest.de.sentry.io/4510668137496656';

/**
 * Initialize Sentry error monitoring
 * Call this at the top of main.tsx before rendering
 */
export function initSentry() {
  // Skip initialization if DSN is not set (e.g., in test environment)
  if (!SENTRY_DSN) {
    console.log('[Sentry] Initialization skipped - no DSN configured');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment detection
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT 
      || (import.meta.env.PROD ? 'production' : 'development'),
    
    // Release version for tracking deployments
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA 
      || import.meta.env.VITE_APP_VERSION 
      || 'local-dev',

    // Tunneling to bypass ad blockers
    tunnel: '/api/tunnel',

    // ============================================
    // Performance Monitoring
    // ============================================
    integrations: [
      // Browser performance tracing
      Sentry.browserTracingIntegration({
        // Track navigation and page loads
        enableInp: true,
      }),
      // Session Replay for debugging
      Sentry.replayIntegration({
        // Mask all text for privacy
        maskAllText: false,
        // Block all media for privacy
        blockAllMedia: false,
      }),
    ],

    // Sampling rates
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0, // 20% in production
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% when error occurs

    // ============================================
    // Privacy & Data Collection
    // ============================================
    // Collect default PII (IP address, etc.) - GDPR compliant with DE region
    sendDefaultPii: true,

    // ============================================
    // Error Filtering
    // ============================================
    ignoreErrors: [
      // Browser extension errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Common non-actionable errors
      'Non-Error promise rejection captured',
      // Network errors that may be transient
      'Load failed',
      'Failed to fetch',
      // User aborted requests
      'AbortError',
      'The user aborted a request',
    ],

    // Filter out events from browser extensions
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
    ],

    // ============================================
    // Event Processing
    // ============================================
    beforeSend(event, hint) {
      // Log errors in development
      if (import.meta.env.DEV) {
        console.error('[Sentry] Capturing event:', hint.originalException || event);
      }

      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_ENABLED_IN_DEV) {
        console.log('[Sentry] Event not sent (development mode)');
        return null;
      }

      return event;
    },

    // Process breadcrumbs before sending
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy console logs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }
      return breadcrumb;
    },
  });

  console.log('[Sentry] Initialized successfully');
}

// ============================================
// User Context Management
// ============================================

interface SentryUser {
  id: string;
  email?: string;
  companyName?: string;
  contactId?: string;
}

/**
 * Set user context after successful login
 * @param user - B2B user information
 */
export function setSentryUser(user: SentryUser) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    // Custom attributes
    company_name: user.companyName,
    contact_id: user.contactId,
  });
  
  // Set additional context
  Sentry.setTag('user_type', 'b2b_customer');
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setTag('user_type', undefined);
}

// ============================================
// Manual Error Capture
// ============================================

/**
 * Manually capture an exception with additional context
 * @param error - Error object
 * @param context - Additional context data
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message/event
 * @param message - Message to capture
 * @param level - Severity level
 * @param context - Additional context
 */
export function captureMessage(
  message: string, 
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
) {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

// ============================================
// Custom Context & Tags
// ============================================

/**
 * Set custom context for debugging
 * @param name - Context name
 * @param context - Context data
 */
export function setContext(name: string, context: Record<string, unknown>) {
  Sentry.setContext(name, context);
}

/**
 * Set a tag for filtering in Sentry dashboard
 * @param key - Tag key
 * @param value - Tag value
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// ============================================
// Transaction Tracking
// ============================================

/**
 * Start a custom transaction for performance monitoring
 * @param name - Transaction name
 * @param op - Operation type
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({
    name,
    op,
  });
}

// Re-export Sentry for advanced usage
export { Sentry };
